import type { ErpModule } from '@/api/platform.api';
import { REQUIRED_PASSWORD_CHANGE_PATH } from '@/constants/authRoutes';
import { resolveAccessibleLandingPath } from '@/utils/pageAccessEngine';
import {
  isOrgOwner,
  resolveDefaultAccessibleWorkspaceModule,
  resolveOwnerHomePath,
} from '@/utils/moduleAccess';
import { getPostLoginPath } from '@/utils/moduleScope';

type PostLoginResult = {
  permissions?: string[];
  roleCode?: string | null;
  enabledModules?: string[];
  accessibleModules?: string[];
  entryModule?: string | null;
  masterAdmin?: boolean;
  forcePasswordChange?: boolean;
};

/**
 * After sign-in, land on the first application this user may open (role ∩ subscription),
 * not every module the organization has enabled.
 */
export function resolvePostLoginDestination(
  result: PostLoginResult,
  redirect?: string | null,
): string {
  if (result.forcePasswordChange) {
    return REQUIRED_PASSWORD_CHANGE_PATH;
  }

  if (redirect?.startsWith('/')) {
    return redirect;
  }

  const permissions = result.permissions ?? [];
  const roleCode = result.roleCode;
  const enabledModules = result.enabledModules ?? [];
  const accessibleModules = result.accessibleModules;
  const entryModule = (result.entryModule as ErpModule | null) ?? null;

  if (isOrgOwner(permissions, roleCode)) {
    if (entryModule) {
      return getPostLoginPath(entryModule, resolveOwnerHomePath(enabledModules), {
        permissions,
        accessibleModules,
        roleCode,
      });
    }
    return resolveOwnerHomePath(enabledModules);
  }

  const preferred =
    entryModule ??
    resolveDefaultAccessibleWorkspaceModule(
      enabledModules,
      permissions,
      accessibleModules,
      roleCode,
    );

  return resolveAccessibleLandingPath(
    {
      permissions,
      roleCode,
      enabledModules,
      accessibleModules,
      entryModule: preferred,
    },
    preferred,
  );
}
